import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { HISTORY_SEASONS, LEAGUE } from '@ff/core';
import { useSeasonSelect } from '../lib/seasonSelect.tsx';
import { useUnread } from '../lib/unread.tsx';
import { UserMenu } from './UserMenu.tsx';

interface Tab {
  to: string;
  label: string;
  end?: boolean;
}

/**
 * The tabs.
 *
 * Deliberately short. Standings, the points grid and the side pots were three
 * tabs answering one question, and an award with its own page is reached by
 * clicking its ticket on the standings page rather than by spending a tab on
 * it — a row too wide to read is a row nobody reads.
 */
function tabs(): Tab[] {
  return [
    { to: '/', label: 'Home', end: true },
    { to: '/standings', label: 'Standings' },
    { to: '/book', label: LEAGUE.bookName },
    { to: '/draft-results', label: 'Draft Results' },
    { to: '/recap', label: 'Recap' },
    { to: '/tip-sheet', label: 'Tip Sheet' },
    // A league with nothing on file gets no tab rather than a page that
    // apologises for itself.
    ...(HISTORY_SEASONS.length > 0 ? [{ to: '/history', label: 'History' }] : []),
  ];
}

export function Shell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const unread = useUnread();
  // Unread smack shows against the board it's on: game weeks under Home, the
  // pre-season board under Draft Results.
  const countFor = (to: string) =>
    to === '/' ? unread.scoreboardTotal : to === '/draft-results' ? unread.warRoomTotal : 0;
  const anyUnread = unread.scoreboardTotal + unread.warRoomTotal > 0;

  // Every tab is shown all season: Draft Results used to disappear at kickoff,
  // but it holds the draft board, which is a season-long reference.
  const tabList = tabs();

  // Close the mobile menu on navigation.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap rounded-md px-3 py-1.5 font-mono text-xs uppercase tracking-wider transition-colors ${
      isActive ? 'bg-felt-raised text-chalk' : 'text-chalk-dim hover:text-chalk'
    }`;

  // Almost every page here is a table or a grid of cards — standings, the points
  // grid, the money board, an expanded matchup — and all of them read better with
  // the width a large monitor actually has. Prose is the exception, so the one
  // long-form thing in the app (the recap) constrains itself rather than the
  // shell constraining everything else to protect it.
  return (
    <div className="mx-auto flex min-h-dvh max-w-5xl flex-col px-4 sm:px-6 xl:max-w-6xl">
      <nav className="sticky top-0 z-20 -mx-4 mb-8 border-b border-felt-line bg-felt/85 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex items-center justify-between gap-4">
          {/* Mobile: hamburger. The tab row is too wide for a phone, so it
              collapses behind this. */}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-chalk-dim hover:text-chalk sm:hidden"
            aria-expanded={menuOpen}
            aria-label="Menu"
          >
            <span className="relative">
              <Hamburger open={menuOpen} />
              {/* The tab row is hidden on a phone, which is exactly the device
                  that got the push, so the count has to show through here. */}
              {anyUnread && !menuOpen && (
                <span
                  className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-brass"
                  aria-hidden="true"
                />
              )}
            </span>
            <span className="font-mono text-xs uppercase tracking-wider">
              {activeLabel(tabList, location.pathname)}
            </span>
          </button>

          {/* Desktop: the full tab row. */}
          <div className="hidden items-center gap-1 sm:flex">
            {tabList.map((t) => (
              <NavLink key={t.to} to={t.to} end={t.end} className={tabClass}>
                <TabLabel label={t.label} count={countFor(t.to)} />
              </NavLink>
            ))}
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <UserMenu />
          </div>
        </div>

        {/* Mobile menu panel. */}
        {menuOpen && (
          <div className="mt-2 grid gap-1 sm:hidden">
            {tabList.map((t) => (
              <NavLink key={t.to} to={t.to} end={t.end} className={tabClass}>
                <TabLabel label={t.label} count={countFor(t.to)} />
              </NavLink>
            ))}
          </div>
        )}
      </nav>

      <PastSeasonBanner />

      <main className="flex-1 pb-10">
        <Outlet />
      </main>

          {/* A quiet, permanent dedication — when the league has one. */}
          {LEAGUE.dedication && (
            <footer className="mt-8 border-t border-felt-line py-6 text-center">
              <p className="font-editorial text-sm italic text-chalk-dim">
                {LEAGUE.dedication}
              </p>
            </footer>
          )}
    </div>
  );
}

/**
 * A banner shown when a past season is open (chosen from History). With the
 * year dropdown gone, this is how you know you're looking backward — and how
 * you get back to the current season.
 */
function PastSeasonBanner() {
  const { seasons, selected, setSelected } = useSeasonSelect();
  const location = useLocation();
  const current = seasons?.[0]; // fetchSeasons returns newest first
  if (current == null || selected == null) return null;
  // On History itself the "current season" framing doesn't apply — it's all-time.
  if (selected === current || location.pathname === '/history') return null;

  return (
    <div className="mb-6 flex items-center justify-between gap-3 rounded-md border border-brass/40 bg-brass/10 px-3 py-2">
      <span className="font-mono text-xs text-chalk">
        Viewing the <span className="font-bold text-brass">{selected}</span> season.
      </span>
      <button
        onClick={() => setSelected(current)}
        className="shrink-0 font-mono text-[0.7rem] uppercase tracking-wider text-brass hover:opacity-80"
      >
        Back to {current} →
      </button>
    </div>
  );
}

/** A tab's name, with the unread count riding on it when there is one. */
function TabLabel({ label, count }: { label: string; count: number }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {label}
      {count > 0 && (
        <span
          className="inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-brass px-1 font-mono text-[0.6rem] font-bold normal-case tracking-normal text-felt tabular-nums"
          aria-label={`${count} unread`}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </span>
  );
}

function activeLabel(tabs: Tab[], path: string): string {
  const match = tabs.find((t) => (t.end ? path === t.to : path.startsWith(t.to) && t.to !== '/'));
  return match?.label ?? 'Menu';
}

function Hamburger({ open }: { open: boolean }) {
  return (
    <span className="relative block h-4 w-5" aria-hidden>
      <span
        className={`absolute left-0 h-0.5 w-5 bg-current transition-all ${
          open ? 'top-2 rotate-45' : 'top-1'
        }`}
      />
      <span
        className={`absolute left-0 top-2 h-0.5 w-5 bg-current transition-opacity ${
          open ? 'opacity-0' : 'opacity-100'
        }`}
      />
      <span
        className={`absolute left-0 h-0.5 w-5 bg-current transition-all ${
          open ? 'top-2 -rotate-45' : 'top-3'
        }`}
      />
    </span>
  );
}
